import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import NumberFormat from 'react-number-format';
import { ArrowRight, BadgeCheck, CalendarDays, ChevronDown, FileText, RotateCcw, Target, Users } from 'lucide-react';
import { toggleControl } from '../../../../store/control/duck';
import { MDRT_CALCULATOR_DEFAULT_STATES } from '../../../../constants/mdrt-calculator';
import { TARGET_TYPE } from '../../../../constants/businessTarget';
import MDRTEquation from './MDRTEquation';
import { SlidingSwitch } from '../../../ClientEntry/ChatGPTPresetDrawer/ChatGPTIceBreakingDrawerV2/SuggestionTypeSwitchButton';
import './MDRTActivityCalculator.scss';

const TIERS = [
  { id: 'MDRT', key: 'isMDRT', multiplier: 1 },
  { id: 'COT', key: 'isCOT', multiplier: 3 },
  { id: 'TOT', key: 'isTOT', multiplier: 6 }
];
const NO_TIER = { isMDRT: 'No', isCOT: 'No', isTOT: 'No' };
// On average, this many SOS Papers (救命紙) handed to clients turn into one closed case.
const SOS_PAPERS_PER_CASE = 3.8;
const WORKING_MONTHS = 10;
const WORKING_WEEKS = WORKING_MONTHS * 4;
const WORKING_DAYS = WORKING_WEEKS * 5;
const MONEY_FORMAT = new Intl.NumberFormat('en-HK', { maximumFractionDigits: 0 });
const ACTIVITY_FORMAT = new Intl.NumberFormat('en-HK', { maximumFractionDigits: 1 });
const money = value => MONEY_FORMAT.format(value);
const activity = value => ACTIVITY_FORMAT.format(value);
const DASH = '—';

const NumberField = ({ id, label, value, onChange, suffix, hint, max = Number.MAX_SAFE_INTEGER }) => (
  <div className="mdrt-cal-field">
    <div className="mdrt-cal-field-label">
      <label htmlFor={id}>{label}</label>
      {hint && <small id={`${id}-hint`}>{hint}</small>}
    </div>
    <div className="mdrt-cal-input">
      <NumberFormat
        id={id}
        value={value == null ? '' : value}
        thousandSeparator
        allowNegative={false}
        decimalScale={2}
        inputMode="decimal"
        aria-describedby={hint ? `${id}-hint` : undefined}
        onFocus={event => event.target.select()}
        isAllowed={({ floatValue }) => floatValue === undefined || (Number.isFinite(floatValue) && floatValue <= max)}
        onValueChange={({ floatValue }, { source }) => {
          if (source === 'event') onChange(floatValue === undefined ? null : floatValue);
        }}
      />
      {suffix && <span>{suffix}</span>}
    </div>
  </div>
);

export const MDRTActivityCalculator = ({ intl, mdrtActivityCalculator, toggleControl }) => {
  const values = { ...MDRT_CALCULATOR_DEFAULT_STATES, ...mdrtActivityCalculator };
  // The dialog's existing state uses quarterly amounts. Convert at this boundary
  // so reopening/resetting remains compatible. The goal is monthly; activities are weekly.
  const monthlyTarget = values.quarterTarget == null ? null : Number(values.quarterTarget) / 3;
  const avgCaseSize = Number(values.avgCaseSize);
  const closingRate = Number(values.closingRate);
  const selectedTier = TIERS.find(tier => values[tier.key] === 'Yes' || values[tier.key] === 'YesP');
  const basis = selectedTier ? (values[selectedTier.key] === 'YesP' ? 'FYP' : 'FYC') : values.basis;
  const t = (id, params) => intl.formatMessage({ id }, params);
  const rateHint =
    Number.isFinite(closingRate) && closingRate > 0 && closingRate <= 100
      ? t('mdrt-cal-rate-ratio', {
          total: activity(100 / closingRate),
          closed: '1'
        })
      : null;
  // Replace rather than merge, so a cleared field (null) overwrites its previous value.
  const update = changes => toggleControl('mdrtActivityCalculator', { ...values, ...changes });
  const selectTarget = (tier, nextBasis = basis) => {
    update({
      ...NO_TIER,
      basis: nextBasis,
      ...(tier ? { [tier.key]: nextBasis === 'FYC' ? 'Yes' : 'YesP' } : {}),
      quarterTarget: tier
        ? ((nextBasis === 'FYC' ? TARGET_TYPE.MDRTC : TARGET_TYPE.MDRTP) * tier.multiplier) / 4
        : values.defaultQuarterTarget
    });
  };

  const canCalculate =
    Number.isFinite(monthlyTarget) &&
    monthlyTarget > 0 &&
    Number.isFinite(avgCaseSize) &&
    avgCaseSize > 0 &&
    Number.isFinite(closingRate) &&
    closingRate > 0 &&
    closingRate <= 100;
  // Match the reference calculator: one quarter is 13 weeks.
  const weeklyCases = canCalculate ? (monthlyTarget * 3) / 13 / avgCaseSize : null;
  const weeklyMeetings = canCalculate ? weeklyCases / (closingRate / 100) : null;
  const dailyMeetings = weeklyMeetings === null ? null : weeklyMeetings / 5;
  // The illustration is its own example (the recruitment pitch): the yearly goal is 12 × the monthly one,
  // worked in a 10-month year of 4 five-day weeks. It is not meant to match the weekly figures above.
  const yearlyCases = canCalculate ? (monthlyTarget * 12) / avgCaseSize : null;
  const yearlyMeetings = canCalculate ? yearlyCases / (closingRate / 100) : null;
  const weeklySosPapers = canCalculate ? weeklyCases * SOS_PAPERS_PER_CASE : null;
  // A cleared or invalid field shows a dash instead of removing the cards, so they keep their place and open state.
  const show = (value, format) => (value === null ? DASH : format(value));
  const hasCase = Number.isFinite(avgCaseSize) && avgCaseSize > 0;

  return (
    <div className="mdrt-calculator">
      <header className="mdrt-cal-header">
        <h2>
          <Target size={20} aria-hidden="true" />
          {t('mdrt-cal-title')}
        </h2>
        <button className="mdrt-cal-reset" type="button" onClick={() => update({ ...MDRT_CALCULATOR_DEFAULT_STATES })}>
          <RotateCcw size={14} aria-hidden="true" />
          {t('mdrt-cal-reset')}
        </button>
      </header>

      <div className="mdrt-cal-controls">
        <SlidingSwitch
          compact
          ariaLabel={t('mdrt-cal-targets')}
          current={selectedTier ? selectedTier.id : 'custom'}
          options={[
            { value: 'custom', label: t('mdrt-cal-custom') },
            ...TIERS.map(tier => ({ value: tier.id, label: tier.id }))
          ]}
          onAction={next => selectTarget(TIERS.find(tier => tier.id === next) || null)}
        />
        <SlidingSwitch
          compact
          ariaLabel={t('mdrt-cal-basis')}
          current={basis}
          options={['FYC', 'FYP'].map(option => ({
            value: option,
            label: t(`mdrt-cal-basis-${option.toLowerCase()}`),
            ariaLabel: t(`mdrt-cal-${option.toLowerCase()}`)
          }))}
          onAction={option => {
            if (selectedTier) selectTarget(selectedTier, option);
            else update({ basis: option });
          }}
        />
      </div>

      <form className="mdrt-cal-fields" onSubmit={event => event.preventDefault()} autoComplete="off">
        <NumberField
          id="mdrt-cal-target"
          label={t(basis === 'FYC' ? 'mdrt-cal-monthly-target' : 'mdrt-cal-monthly-target-premium')}
          value={monthlyTarget}
          onChange={value => {
            const quarterTarget = value == null ? null : value * 3;
            update({ ...NO_TIER, basis, quarterTarget, defaultQuarterTarget: quarterTarget });
          }}
        />
        <NumberField
          id="mdrt-cal-case"
          label={t(basis === 'FYC' ? 'mdrt-cal-average' : 'mdrt-cal-average-premium')}
          value={values.avgCaseSize}
          onChange={value => update({ avgCaseSize: value })}
        />
        <NumberField
          id="mdrt-cal-rate"
          label={t('mdrt-cal-rate')}
          hint={rateHint}
          value={values.closingRate}
          suffix="%"
          max={100}
          onChange={value => update({ closingRate: value })}
        />
      </form>

      <section
        className="mdrt-cal-activity"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={t('mdrt-cal-activity')}
      >
        <h3>
          <CalendarDays size={16} aria-hidden="true" />
          {t('mdrt-cal-activity')}
        </h3>
        <div className="mdrt-cal-flow">
          <div className="mdrt-cal-node is-meetings">
            <span className="mdrt-cal-node-label">
              <Users size={18} aria-hidden="true" />
              {t('mdrt-cal-meetings')}
            </span>
            <strong data-testid="mdrt-cal-meetings">{weeklyMeetings === null ? '—' : activity(weeklyMeetings)}</strong>
            <small className="mdrt-cal-daily-note" data-testid="mdrt-cal-daily">
              ({t('mdrt-cal-daily', { meetings: dailyMeetings === null ? '—' : activity(dailyMeetings) })})
            </small>
          </div>
          <div className="mdrt-cal-arrow" aria-hidden="true">
            {rateHint && <span data-testid="mdrt-cal-flow-ratio">{rateHint}</span>}
            <ArrowRight size={24} />
          </div>
          <div className="mdrt-cal-node is-goal">
            <span className="mdrt-cal-node-label">
              <Target size={18} aria-hidden="true" />
              {t(basis === 'FYC' ? 'mdrt-cal-achieved-target' : 'mdrt-cal-premium-target')}
            </span>
            <strong data-testid="mdrt-cal-monthly">{money(monthlyTarget || 0)}</strong>
            <small className="mdrt-cal-annual-note">
              (<span>{t(basis === 'FYC' ? 'mdrt-cal-yearly-income' : 'mdrt-cal-yearly-premium')}</span>{' '}
              <span data-testid="mdrt-cal-yearly">{money((monthlyTarget || 0) * 12)}</span>)
            </small>
          </div>
        </div>
        {!canCalculate && (
          <p className="mdrt-cal-weekly" data-testid="mdrt-cal-weekly">
            {t('mdrt-cal-empty')}
          </p>
        )}
      </section>

      <details className="mdrt-cal-sos">
        <summary>
          <FileText size={16} aria-hidden="true" />
          {t('mdrt-cal-sos-title')}
          <ChevronDown size={14} aria-hidden="true" />
        </summary>
        <div className="mdrt-cal-sos-concept">
          <div className="mdrt-cal-sos-papers" aria-hidden="true">
            {Array.from({ length: Math.ceil(SOS_PAPERS_PER_CASE) }, (_, index) => {
              const share = Math.min(1, SOS_PAPERS_PER_CASE - index);
              return (
                <span className={`mdrt-cal-sos-paper${share < 1 ? ' is-partial' : ''}`} key={index}>
                  <FileText size={48} strokeWidth={1.6} />
                  {share < 1 && (
                    <span className="mdrt-cal-sos-paper-fill" style={{ width: `${share * 100}%` }}>
                      <FileText size={48} strokeWidth={1.6} />
                    </span>
                  )}
                </span>
              );
            })}
          </div>
          <ArrowRight className="mdrt-cal-sos-arrow" size={22} aria-hidden="true" />
          <div className="mdrt-cal-sos-case">
            <BadgeCheck size={44} aria-hidden="true" />
            <span>{t('mdrt-cal-sos-one-case')}</span>
          </div>
        </div>
        <p className="mdrt-cal-sos-caption" data-testid="mdrt-cal-sos-concept">
          {t('mdrt-cal-sos-per-case', { papers: SOS_PAPERS_PER_CASE })}
        </p>
        <div className="mdrt-cal-sos-total">
          <span className="mdrt-cal-sos-label">{t('mdrt-cal-sos-weekly')}</span>
          <strong data-testid="mdrt-cal-sos-weekly">{show(weeklySosPapers, activity)}</strong>
          <small data-testid="mdrt-cal-sos-note">
            (
            {t('mdrt-cal-sos-note', {
              daily: show(weeklySosPapers, value => activity(value / 5))
            })}
            )
          </small>
        </div>
      </details>
      <MDRTEquation
        t={t}
        rate={rateHint ? `${rateHint} (${closingRate}%)` : DASH}
        ratio={rateHint || DASH}
        commission={hasCase ? `HKD ${money(avgCaseSize)}` : DASH}
        commissionMessageId={basis === 'FYC' ? 'mdrt-cal-eq-commission' : 'mdrt-cal-eq-premium'}
        daily={show(yearlyMeetings, value => activity(value / WORKING_DAYS))}
        weekly={show(yearlyMeetings, value => activity(value / WORKING_WEEKS))}
        year={show(yearlyMeetings, activity)}
        cases={show(yearlyCases, activity)}
        incomeLabel={t(basis === 'FYC' ? 'mdrt-cal-yearly-income' : 'mdrt-cal-yearly-premium')}
        income={monthlyTarget > 0 ? `HKD ${money(monthlyTarget * 12)}` : DASH}
        months={WORKING_MONTHS}
        latin={!/^zh/.test(intl.locale)}
      />
    </div>
  );
};

const container = connect(state => ({ mdrtActivityCalculator: state.control.mdrtActivityCalculator }), {
  toggleControl
})(MDRTActivityCalculator);
export default injectIntl(container);
